<template>
  <div class="delete-account-section">
    <div class="middle-container col-xs-12">
      <h4 class="delete-account-section__headline">
        {{ $t("headline") }}
      </h4>
      <i18n
        v-if="isSubscriptionPartnerUser"
        path="subscriptionPartnerDescription"
        tag="p"
        class="delete-account-section__text"
      >
        <b>{{ $t("subscriptionPartnerHighlight") }}</b>
      </i18n>
      <i18n v-else path="description" tag="p" class="delete-account-section__text">
        <b>{{ $t("highlight") }}</b>
      </i18n>

      <button
        class="delete-account-section__delete"
        :class="{
          'delete-account-section__delete--active': canDeleteAccount
        }"
        @click="onDeleteClick"
      >
        {{ $t("delete") }}
      </button>
      <p class="delete-account-section__privacy-note">
        <strong>{{ $t("privacyNote.title") }}</strong>
        {{ $t("privacyNote.text") }}
      </p>
    </div>
    <delete-account-modal />
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import DeleteAccountModal from "./modal"

export default {
  name: "DeleteAccountSection",
  components: {
    DeleteAccountModal
  },
  data() {
    return {
      showSubscription: false,
      showConfirmation: false
    }
  },
  computed: {
    ...mapGetters("settings", ["canDeleteAccount"]),
    ...mapGetters(["isSubscriptionPartnerUser"])
  },
  methods: {
    onDeleteClick() {
      if (this.canDeleteAccount) {
        this.$store.commit("settings/currentStepDeleteAccount", "form")
      }
    }
  },
  i18n: {
    messages: {
      en: {
        headline: "Delete your account",
        description:
          "Please note that deleting your account will delete all your content, your library and your completion history. {0} Please make sure to cancel your subscription first, before you delete your account. You can do so right here in your account settings.",
        highlight: "Please note that deleting your account will not automatically end a purchased subscription.",
        subscriptionPartnerDescription:
          "Please note that deleting your account will delete all your content, your library and your completion history. {0} You activated your subscription through an external partner. Please contact our customer support to cancel your subscription before deleting your account.",
        subscriptionPartnerHighlight:
          "Please note that deleting your account will not automatically end your subscription.",
        privacyNote: {
          title: "Note on Privacy:",
          text: "Deleting your account will also remove your associated personal data from our records. Please note that we may be legally obliged under tax and commercial law to retain certain accounting-related information (in particular invoices). It may therefore not currently be possible for us to delete all your data. However, we only store the remaining data to fulfil our legal obligations. Once these periods have expired, we will delete your remaining data immediately without you having to ask us to do so again."
        },
        delete: "Delete account"
      },
      de: {
        headline: "Account löschen",
        description:
          "Bitte beachte, dass beim Löschen deines Kontos alle deine Inhalte, deine Bibliothek und deine beendeten Titel gelöscht werden. {0} Bitte stelle sicher, dass du zuerst dein Abonnement kündigst, bevor du dein Konto löschst. Das kannst du direkt hier in deinen Kontoeinstellungen tun.",
        highlight:
          "Bitte beachte, dass das Löschen deines Kontos nicht automatisch das Ende eines gekauften Abonnements bedeutet.",
        subscriptionPartnerDescription:
          "Bitte beachte, dass beim Löschen deines Kontos alle deine Inhalte, deine Bibliothek und deine beendeten Titel gelöscht werden. {0} Du hast dein Abonnement über einen externen Partner aktiviert. Bitte kontaktiere unseren Kundensupport, um dein Abonnement zu kündigen, bevor du dein Konto löschst.",
        subscriptionPartnerHighlight:
          "Bitte beachte, dass die Löschung deines Kontos nicht automatisch das Ende deines Abonnements bedeutet.",
        privacyNote: {
          title: "Hinweis zum Datenschutz:",
          text: "Wenn du dein Konto löschst, werden auch deine zugehörigen persönlichen Daten aus unseren Unterlagen entfernt. Bitte beachte, dass wir steuer- und handelsrechtlich verpflichtet sein können, bestimmte buchhaltungsrelevante Informationen (insbesondere Rechnungen) aufzubewahren. Daher kann es sein, dass es uns derzeit nicht möglich ist, alle deine Daten zu löschen. Wir speichern jedoch nur die verbleibenden Daten, um unsere gesetzlichen Verpflichtungen zu erfüllen. Wenn diese Fristen abgelaufen sind, löschen wir deine restlichen Daten sofort, ohne dass du uns erneut darum bitten musst."
        },
        delete: "Jetzt Account löschen"
      }
    }
  }
}
</script>

<style lang="scss">
.delete-account-section {
  @include font-family(sans-serif);

  &__headline {
    @include font-style(h4);

    margin-bottom: $spacing-24;
  }

  &__text {
    @include font-style(p);

    margin-bottom: $spacing-24;
  }

  &__delete {
    @include font-family(sans-serif);
    @include font-style(button, $color: $light-grey);

    margin-bottom: $spacing-24;

    &--active {
      @include font-style(button, $color: $raspberry, $weight: "bold");

      text-decoration: underline;

      cursor: pointer;

      &:hover {
        color: $raspberry2;
      }

      &:focus-visible {
        border-radius: $spacing-4;
        outline-offset: $spacing-8;
        outline-color: $blinkist-blue;
        outline-style: solid;
        outline-width: 0.125rem;
      }
    }
  }

  &__privacy-note {
    @include font-style(legal);
  }
}
</style>
